import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { Observable } from 'rxjs';
import { ExportColumnDef } from '../../models/export-column-def.model';
import { TranslationService } from '../../translations/services/translation.service';

export interface ExportModalData {
  columns: ExportColumnDef[];
  dataFetcher?: () => Observable<any>;
  fileNamePrefix: string;
  totalRecords?: number;
  maxExportRows?: number;
  excelExportCallback?: (selectedColumnNames: Record<string, string>, fileName: string) => void;
  pdfExportCallback?: () => void;
  extraColumns?: Record<string, string>;
}

@Component({
  selector: 'app-export-modal',
  templateUrl: './export-modal.component.html',
  styleUrl: './export-modal.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private alertService = inject(AlertService);
  public data = inject<ExportModalData>(MAT_DIALOG_DATA);
  public dialogRef = inject<MatDialogRef<ExportModalComponent>>(MatDialogRef);
  public translationService = inject(TranslationService);

  columnsForm!: FormArray<FormControl<boolean>>;
  fileName!: string;

  ngOnInit(): void {
    this.columnsForm = this.fb.array(
      this.data.columns.map((col) => this.fb.nonNullable.control<boolean>(col.selected ?? true))
    ) as FormArray<FormControl<boolean>>;
    this.fileName = this.data.fileNamePrefix + '-' + new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  }

  get allSelected(): boolean {
    return this.columnsForm?.controls.length > 0 && this.columnsForm.controls.every((ctrl) => ctrl.value);
  }

  get selectedColumnNames(): Record<string, string> {
    const result: Record<string, string> = {};
    if (!this.columnsForm) {
      return result;
    }
    this.data.columns.forEach((col, i) => {
      if (this.columnsForm.at(i).value) {
        result[col.key] = col.label;
      }
    });
    return result;
  }

  selectAll(): void {
    this.columnsForm.controls.forEach((ctrl) => ctrl.setValue(true));
    this.cdr.markForCheck();
  }

  deselectAll(): void {
    this.columnsForm.controls.forEach((ctrl) => ctrl.setValue(false));
    this.cdr.markForCheck();
  }

  onPdfExport(): void {
    if (this.data.pdfExportCallback) {
      this.data.pdfExportCallback();
    }
    this.dialogRef.close();
  }

  onExcelExport(): void {
    const selectedColumnNames = this.selectedColumnNames;
    if (!selectedColumnNames || Object.keys(selectedColumnNames).length === 0) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('selectAtleastOneValueErrorText')
      } as Alert);
      return;
    }

    if (this.data.excelExportCallback) {
      this.data.excelExportCallback(selectedColumnNames, this.fileName);
    }
    this.dialogRef.close();
  }

  onSharedExcelExportStarted(): void {
    this.dialogRef.close();
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
